import type { FormField } from '@lucid/lego';
import type { BusinessSearchResult } from '../../../../../api/services/lupp/order/dto.order';
import {
  getFinancingTypeOptions,
  getFinancingSubtypeOptions,
  getApplicationStatusOptions,
  getApplicationDecisionOptions,
  NOT_APPLICABLE_FALLBACK,
} from './constants';

export type FinancingFormOptions = {
  /** When set, Lienholder Name is a dropdown of these values instead of the D&B search. */
  lienholderOptions?: { label: string; value: string }[];
  /** Embedded-subvention providers hide Amount Financed from partners. */
  isEmbedded?: boolean;
};

type GroupChildField = Exclude<FormField, { fieldType: 'group' }>;

type LienholderFieldParams = {
  labels?: Record<string, string>;
  defaultValue?: string;
  theme: 'light' | 'dark';
  onLienholderChange?: (business: BusinessSearchResult | null) => void;
  isDisabled: boolean;
  lienholderOptions?: FinancingFormOptions['lienholderOptions'];
};

const buildLienholderField = ({
  labels,
  defaultValue,
  theme,
  onLienholderChange,
  isDisabled,
  lienholderOptions,
}: LienholderFieldParams): GroupChildField => {
  if (lienholderOptions) {
    return {
      id: 'lienholderName',
      fieldType: 'dropdown',
      name: 'lienholderName',
      apiKey: 'lienholderName',
      label: labels?.['form.lienholderName'] ?? 'Lienholder Name',
      required: true,
      disabled: isDisabled,
      width: 'third',
      options: lienholderOptions,
      defaultValue,
      validationMessages: {
        required: labels?.['form.fieldRequired'] ?? 'This field is required',
      },
    };
  }

  return {
    id: 'lienholderName',
    fieldType: 'custom',
    component: 'BusinessNameSearch',
    name: 'lienholderName',
    apiKey: 'lienholderName',
    required: true,
    width: 'third',
    componentProps: {
      disabled: isDisabled,
      label: labels?.['form.lienholderName'] ?? 'Lienholder Name',
      placeholder: labels?.['form.searchCompanyPlaceholder'] ?? 'Search for company...',
      required: true,
      dataTestId: 'lienholder-name-search',
      defaultValue: defaultValue
        ? {
            name: defaultValue,
            duns: '',
            primaryName: defaultValue,
            isOutOfBusiness: false,
            country: '',
            countryCode: '',
            displayText: defaultValue,
          }
        : null,
      onChange: onLienholderChange,
      theme: theme === 'dark' ? 'light' : 'dark',
      messages: {
        searchingText: labels?.['form.searching'] ?? 'Searching...',
        apiErrorText:
          labels?.['form.searchUnavailable'] ??
          'Search is temporarily unavailable. Please try again later.',
        minCharsText: labels?.['form.minCharsToSearch'] ?? 'Type at least 3 characters to search',
        noResultsText: labels?.['form.noResults'] ?? 'No matching results found.',
        validationRequiredText:
          labels?.['validation.selectValidCompany'] ??
          'Please select a valid Company from the list.',
      },
    },
    validationMessages: {
      required:
        labels?.['form.fieldRequired'] ??
        labels?.['validation.selectValidCompany'] ??
        'Please select a valid Company from the list.',
    },
  };
};

const buildAmountFinancedField = (
  labels: Record<string, string> | undefined,
  defaultValue: string | undefined,
  isDisabled: boolean,
  isEmbedded: boolean
): GroupChildField => ({
  id: 'amountFinanced',
  fieldType: 'text',
  name: 'amountFinanced',
  apiKey: 'amountFinanced',
  label: labels?.['form.amountFinanced'] ?? 'Amount Financed',
  required: !isEmbedded,
  disabled: isDisabled || isEmbedded,
  width: 'third',
  defaultValue: isEmbedded ? undefined : defaultValue,
  ...(isEmbedded && {
    placeholder: labels?.['financeProvider.notApplicable'] ?? NOT_APPLICABLE_FALLBACK,
  }),
  validationMessages: {
    required: labels?.['form.fieldRequired'] ?? 'This field is required',
  },
});

/**
 * Creates form fields configuration for Financing form
 *
 * @param labels - i18n labels for form field labels and placeholders
 * @param defaultValues - Default values for financing fields
 * @param theme - Theme variant (light or dark)
 * @param onLienholderChange - Callback when business name is selected
 * @param isDisabled - Whether the form should be disabled (based on applicationSource)
 * @param options - Lienholder dropdown options and Embedded-provider state
 * @returns Array of FormField configurations
 */
export const createFinancingFormFields = (
  labels?: Record<string, string>,
  defaultValues?: {
    lienholderName?: string;
    financingType?: string;
    financingSubtype?: string;
    applicationStatus?: string;
    applicationDecision?: string;
    partnerAppId?: string;
    downpayment?: string;
    amountFinanced?: string;
  },
  theme: 'light' | 'dark' = 'dark',
  onLienholderChange?: (business: BusinessSearchResult | null) => void,
  isDisabled: boolean = false,
  options: FinancingFormOptions = {}
): FormField[] => {
  const financingTypeOptions = getFinancingTypeOptions(labels);
  const financingSubtypeOptions = getFinancingSubtypeOptions(labels);
  const applicationStatusOptions = getApplicationStatusOptions(labels);
  const applicationDecisionOptions = getApplicationDecisionOptions(labels);

  return [
    {
      id: 'financingGroup',
      fieldType: 'group',
      title: labels?.['form.financing'] ?? 'Financing',
      width: 'full',
      children: [
        buildLienholderField({
          labels,
          defaultValue: defaultValues?.lienholderName,
          theme,
          onLienholderChange,
          isDisabled: isDisabled || Boolean(defaultValues?.lienholderName),
          lienholderOptions: options.lienholderOptions,
        }),
        {
          id: 'financingType',
          fieldType: 'dropdown',
          name: 'financingType',
          apiKey: 'financingType',
          label: labels?.['form.financingType'] ?? 'Financing Type',
          required: true,
          // Read-only once the quote has set it.
          disabled: isDisabled || Boolean(defaultValues?.financingType),
          width: 'third',
          options: financingTypeOptions,
          defaultValue: defaultValues?.financingType?.toLowerCase(),
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        {
          id: 'financingSubtype',
          fieldType: 'dropdown',
          name: 'financingSubtype',
          apiKey: 'financingSubtype',
          label: labels?.['form.financingSubtype'] ?? 'Financing Subtype',
          required: true,
          disabled: isDisabled || Boolean(defaultValues?.financingSubtype),
          width: 'third',
          options: financingSubtypeOptions,
          defaultValue: defaultValues?.financingSubtype?.toLowerCase(),
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        {
          id: 'applicationStatus',
          fieldType: 'dropdown',
          name: 'applicationStatus',
          apiKey: 'applicationStatus',
          label: labels?.['form.applicationStatus'] ?? 'Application Status',
          required: true,
          disabled: isDisabled,
          width: 'half',
          options: applicationStatusOptions,
          defaultValue: defaultValues?.applicationStatus?.toLowerCase(),
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        {
          id: 'applicationDecision',
          fieldType: 'dropdown',
          name: 'applicationDecision',
          apiKey: 'applicationDecision',
          label: labels?.['form.applicationDecision'] ?? 'Application Decision',
          required: true,
          disabled: isDisabled,
          width: 'half',
          options: applicationDecisionOptions,
          defaultValue: defaultValues?.applicationDecision?.toLowerCase(),
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        {
          id: 'partnerAppId',
          fieldType: 'text',
          name: 'partnerAppId',
          apiKey: 'partnerAppId',
          label: labels?.['form.partnerAppId'] ?? 'Partner App ID',
          required: true,
          disabled: isDisabled,
          width: 'third',
          defaultValue: defaultValues?.partnerAppId,
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        {
          id: 'downpayment',
          fieldType: 'text',
          name: 'downpayment',
          apiKey: 'downpayment',
          label: labels?.['form.downpayment'] ?? 'Downpayment',
          required: true,
          disabled: isDisabled,
          width: 'third',
          defaultValue: defaultValues?.downpayment,
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        buildAmountFinancedField(
          labels,
          defaultValues?.amountFinanced,
          isDisabled,
          options.isEmbedded ?? false
        ),
      ],
    },
    {
      id: 'formButtons',
      fieldType: 'buttonGroup',
      buttons: [
        {
          id: 'submitButton',
          label: labels?.['form.save'] ?? 'Save',
          type: 'primary',
          size: 'medium',
          color: 'neutral',
          theme: 'light',
          isFluid: true,
          disabled: isDisabled,
        },
      ],
    },
  ];
};
